import React, { useEffect } from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../layout/Layout";
import { BackIcon } from "../../icons";
import { getBooking } from "../../../actions/admin/booking";

const TZ = "Asia/Tokyo";

const STATUS_STYLES = {
  PENDING_APPROVAL: { label: "Change Requested", className: "tw:bg-[#20639B1A] tw:text-[#20639B]" },
  REJECTED: { label: "Change Rejected", className: "tw:bg-[#FFEAE7] tw:text-[#EA4335]" },
  APPROVED: { label: "Change Approved", className: "tw:bg-[#DBF4F1] tw:text-[#18B1A3]" },
  DRAFT: { label: "Draft", className: "tw:bg-[#F3F3F3] tw:text-[#8D8D8D]" },
  UPDATED_BY_ADMIN: {label: "Updated by Admin", className: "tw:bg-[#F5EEFF] tw:text-[#8F5CD1]"},
};

const getStatusMeta = (status, responseBy, isAdminEdit = false) => {
  const key = String(status || "").toUpperCase();
  if (key === "APPROVED" && !responseBy) {
    return { label: "Updated", className: "tw:bg-[#F9EDD3] tw:text-[#A3780F]" };
  }

  if(key === "APPROVED" && isAdminEdit) {
    return STATUS_STYLES["UPDATED_BY_ADMIN"];
  }

  return STATUS_STYLES[key] || { label: status || "-", className: "tw:bg-[#F3F3F3] tw:text-[#8D8D8D]" };
};

const toCurrency = (value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) return "-";
  const n = Number(value);
  if (n === 0) return "-";
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n)} JPY`;
};

const formatJSTDate = (value) => {
  if (!value) return ["-", ""];
  const m = moment.tz(value, TZ).locale("en");
  return [m.format("MMM D, YYYY"), m.format("H:mm")];
};

const getPaymentReceived = (row, booking) => {
  const userRole = String(row?.userId?.role || "").toLowerCase();
  if (userRole === "business") return null;

  if (row?.status === "APPROVED") {
    if (row?.paymentStatus === "CHARGED") {
      const prevStatus = row?.previousBooking?.paymentStatus;
      if (prevStatus === "CHARGED" || prevStatus === "ADDED_TO_MONTHLY_TOTAL") {
        return row?.previousBooking?.totalPrice;
      }
      return row?.chargeAmount;
    }
    const prevStatus = row?.previousBooking?.paymentStatus;
    if (prevStatus === "CHARGED" || prevStatus === "ADDED_TO_MONTHLY_TOTAL") {
      return row?.previousBooking?.totalPrice;
    }
    return null;
  }

  if (booking?.paymentStatus === "CHARGED" || booking?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
    return booking?.totalPrice;
  }
  return null;
};

const TrendIcon = ({ trend }) => {
  if (trend === "up") {
    return (
      <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 0L9.33013 7.5H0.669873L5 0Z" fill="#EA4335" />
      </svg>
    );
  }
  if (trend === "down") {
    return (
      <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 12L0.669873 4.5H9.33013L5 12Z" fill="#3CAEA3" />
      </svg>
    );
  }
  return null;
};

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M10.0005 3.75C14.1668 3.75013 17.7248 6.34201 19.1665 10C17.7249 13.6582 14.167 16.2499 10.0005 16.25C5.83382 16.25 2.27516 13.6583 0.833496 10C2.27526 6.34186 5.83396 3.75 10.0005 3.75ZM10.0005 5.83398C7.70063 5.83398 5.83372 7.70019 5.8335 10C5.8335 12.3 7.70049 14.167 10.0005 14.167C12.3003 14.1668 14.1665 12.2999 14.1665 10C14.1663 7.7003 12.3002 5.83416 10.0005 5.83398ZM10.0005 7.5C11.3835 7.50017 12.5003 8.61697 12.5005 10C12.5005 11.3832 11.3837 12.4998 10.0005 12.5C8.61716 12.5 7.50049 11.3833 7.50049 10C7.50071 8.61686 8.61729 7.5 10.0005 7.5Z" fill="currentColor" />
  </svg>
);

const baseTh = "tw:h-[60px] tw:bg-[#282828] tw:px-[15px] tw:py-[20px] tw:text-left tw:text-[14px] tw:font-[700] tw:leading-[22px] tw:text-white tw:whitespace-nowrap";
const baseTd = "tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-[14px] tw:leading-[22px] tw:text-[#282828]";

const AdminModificationHistoryPage = ({ match }) => {
  const bookingId = match?.params?.id;
  const dispatch = useDispatch();
  const { loading, booking } = useSelector((state) => state.booking);

  useEffect(() => {
    if (!bookingId) return;
    dispatch(getBooking(bookingId));
  }, [dispatch, bookingId]);

  const requestRows = Array.isArray(booking?.requestChangeBookings)
    ? booking.requestChangeBookings
    : Array.isArray(booking?.requestChangeBooking)
      ? booking.requestChangeBooking
      : [];

  const sortedRows = [...requestRows].sort(
    (a, b) =>
      new Date(b?.requestCreatedAt || b?.date || b?.createdAt || 0) -
      new Date(a?.requestCreatedAt || a?.date || a?.createdAt || 0)
  );

  const passengerName = `${booking?.customer?.pronounced || ""} ${booking?.customer?.name || "-"}`.trim();

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100" style={{ backgroundColor: "white" }}>
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:pb-[28px] tw:lg:pb-[42px] tw:px-5 tw:pt-8">
            <Link
              to={`/admin/bookinglist/edit/v2/${bookingId}`}
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:text-[#282828] tw:text-[14px]">Back</span>
            </Link>

            <div className="tw:flex tw:flex-col">
              <div className="tw:!font-hanken-grotesk tw:font-[500] tw:text-[14px] tw:text-[#7c7b7b]">
                {`[Cart #${booking?.cartID || ""}・Booking #${booking?.key || ""}]`}
              </div>
              <div className="tw:!font-hanken-grotesk tw:text-[24px] tw:font-[500] tw:leading-normal tw:text-[#212529]">
                Modification History
              </div>
            </div>
          </div>

          {loading ? (
            <div className="tw:px-5 tw:text-[14px] tw:text-[#8D8D8D]">Loading…</div>
          ) : (
            <section className="tw:w-full tw:overflow-x-auto">
              <table className="tw:w-full tw:min-w-[1100px] tw:border-separate tw:border-spacing-1 tw:border tw:border-[#E8E8E8] tw:rounded-[5px]">
                <thead>
                  <tr>
                    {[
                      { key: "editedBy", label: "Edited By", w: "tw:w-[175px] tw:rounded-tl-[5px]" },
                      { key: "on", label: "On", w: "tw:w-[150px]" },
                      { key: "status", label: "Modification Status", w: "tw:w-[180px]" },
                      { key: "payment", label: "Payment Received", w: "tw:w-[155px]" },
                      { key: "newPrice", label: "New Price", w: "tw:w-[160px]" },
                      { key: "adminUpdate", label: "Admin Update", w: "tw:w-[150px]" },
                      { key: "admin", label: "Admin", w: "tw:w-[140px]" },
                      { key: "action", label: "", w: "tw:w-[62px] tw:rounded-tr-[5px] tw:sticky tw:right-0 tw:z-20" },
                    ].map(({ key, label, w }) => (
                      <th key={key} className={`${baseTh} ${w}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length ? (
                    sortedRows.map((row, index) => {
                      const statusMeta = getStatusMeta(row?.status, row?.responseBy, row?.isAdminEdit);
                      const isPending = String(row?.status || "").toUpperCase() === "PENDING_APPROVAL";

                      const editedBy = row?.isAdminEdit ? "TLF Admin" : (passengerName || "-");
                      const [onDate, onTime] = formatJSTDate(row?.requestCreatedAt || row?.date || row?.createdAt);
                      const paymentRaw = getPaymentReceived(row, booking);
                      const newPriceRaw = (row?.editBooking?.totalPrice ?? 0) + (row?.totalCancellationFee ?? 0);
                      const paymentNumeric = paymentRaw !== null ? Number(paymentRaw) : null;
                      const newPriceNumeric = Number(newPriceRaw) || null;
                      const trend = paymentNumeric !== null && newPriceNumeric !== null
                        ? newPriceNumeric > paymentNumeric ? "up" : newPriceNumeric < paymentNumeric ? "down" : "flat"
                        : "flat";
                      const [adminUpdateDate, adminUpdateTime] = isPending ? ["-", ""] : formatJSTDate(row?.respondedAt);
                      // Admin edits carry the acting admin in adminUserId; change-request
                      // responses carry the approving admin in responseBy. Both are
                      // populated (name only) by GET /api/admin/bookings.
                      const adminName = (row?.isAdminEdit
                        ? row?.adminUserId?.name
                        : row?.responseBy?.name) || "-";

                      return (
                        <tr key={row?._id || index} className="tw:!border-b tw:!border-[#E8E8E8]">
                          <td className={baseTd}>{editedBy}</td>
                          <td className={baseTd}>
                            <span className="tw:block">{onDate}</span>
                            {onTime && <span className="tw:block tw:text-[#8D8D8D]">{onTime}</span>}
                          </td>
                          <td className="tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px]">
                            <div className={`tw:inline-flex tw:h-[38px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-4 tw:text-[13px] tw:font-[500] ${statusMeta.className}`}>
                              {statusMeta.label}
                            </div>
                          </td>
                          <td className={baseTd}>{toCurrency(paymentRaw)}</td>
                          <td className={baseTd}>
                            <div className="tw:flex tw:items-center tw:gap-1.5">
                              <span>{toCurrency(newPriceRaw || null)}</span>
                              <TrendIcon trend={trend} />
                            </div>
                          </td>
                          <td className={baseTd}>
                            <span className="tw:block">{adminUpdateDate}</span>
                            {adminUpdateTime && <span className="tw:block tw:text-[#8D8D8D]">{adminUpdateTime}</span>}
                          </td>
                          <td className={baseTd}>{adminName}</td>
                          <td className="tw:!border-s tw:!border-[#E8E8E8] tw:sticky tw:right-0 tw:z-10 tw:h-[75px] tw:border-b tw:border-x tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-center">
                            {row?.requestType === "reschedule" ? (
                              <Link
                                to={{
                                  pathname: `/admin/bookinglist/edit/v2/${bookingId}/modification-history/${row?._id}`,
                                  state: { historyItem: row, booking },
                                }}
                                className="tw:inline-flex tw:!no-underline"
                              >
                                <div className="tw:inline-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-[3px] tw:bg-[#DBBE78] tw:text-white">
                                  <ViewIcon />
                                </div>
                              </Link>
                            ) : (
                              <span className="tw:text-[#8D8D8D] tw:text-[13px]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className={`${baseTd} tw:text-center tw:text-[#8D8D8D]`}>
                        No modification history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default AdminModificationHistoryPage;
